import { ListItemIcon, ListItemText, MenuItem, Typography } from "@mui/material";
import { MessageSeverity } from "@sinch/core/entities/serverEnums";
import { MdsDeleteForever } from "@sinch/icons";
import { parseMutationErrorsFromData } from "@sinch/utils/apollo";
import { useSnackbar } from "notistack";
import { propEq } from "ramda";
import { isNotNilOrEmpty } from "ramda-adjunct";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { MdsIcon } from "@sinch/components";
import { useRatelist } from "@sinch/components/application/Rate/useRatelist";
import { ConfirmDialogWrapper } from "@sinch/components/ConfirmDialogWrapper/ConfirmDialogWrapper";
import { RatelistType } from "@sinch/types/sinch.types";

export const RemoveDefaultForCompanyMenuItem = ({
  type,
  companyId,
  onPerformedAction,
}: {
  type: RatelistType;
  companyId: string;
  onPerformedAction: () => void;
}) => {
  const { t } = useTranslation(["common", "draft"]);
  const { removeDefaultForCompany } = useRatelist();
  const { enqueueSnackbar } = useSnackbar();

  const handleConfirm = useCallback(async () => {
    try {
      const response = await removeDefaultForCompany({ variables: { ratelistType: type, companyId } });
      const firstMessage = parseMutationErrorsFromData(response).find((message) =>
        propEq("severity", MessageSeverity.Error)(message)
      )?.message;
      if (isNotNilOrEmpty(firstMessage)) {
        enqueueSnackbar(firstMessage, { variant: "error" });
      }
      onPerformedAction();
    } catch {
      enqueueSnackbar(t("ratelistError"), { variant: "error" });
    }
  }, [removeDefaultForCompany, type, companyId, onPerformedAction, enqueueSnackbar, t]);

  return (
    <ConfirmDialogWrapper
      content={<Typography>{t("CompanyRatelist.confirmRemoveDefaultForCompany")}</Typography>}
      label={t("RateBuilder.confirmRemoveDefaultTitle")}
      onConfirm={handleConfirm}
      render={({ openConfirmation }) => (
        <MenuItem color="error" onClick={openConfirmation}>
          <ListItemIcon>
            <MdsIcon color="error" icon={MdsDeleteForever} />
          </ListItemIcon>
          <ListItemText sx={{ color: "error.main" }}>{t("CompanyRatelist.removeDefaultForCompany")}</ListItemText>
        </MenuItem>
      )}
    />
  );
};
